/**
 * The one door to the HTTP API (docs/api-contract.md).
 *
 * Rules this file enforces so no page has to think about them:
 *   - Base path `/api/v1`, JSON in, JSON out.
 *   - `credentials: 'include'` so the session cookie always rides along.
 *   - The active brand lives on the server session. Nothing here ever sends a
 *     brand id, and callers must not add one.
 *   - Every non-2xx response becomes an `ApiError` carrying the wire `code`,
 *     `message`, `details` and the HTTP `status`, because a quota denial and a
 *     suppression block are normal outcomes the UI has to explain, not crashes.
 */
import type { ErrorEnvelope, ErrorWireCode } from './types'

/** Base path for every call. The dev server proxies `/api` to the API origin. */
export const API_BASE = '/api/v1'

/** Value kinds accepted in a query string. Arrays repeat the key. */
export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[]
export type QueryParams = Record<string, QueryValue>

export interface ApiErrorInit {
  code: string
  message: string
  status: number
  details?: Record<string, unknown>
  path: string
  method: string
  cause?: unknown
}

/**
 * A failure the UI is expected to render.
 *
 * `code` is the machine-readable enum from the API. Switch on it, never on the
 * message text. Two synthetic codes exist for problems that never reach the
 * server: `NETWORK_UNREACHABLE` and `RESPONSE_UNREADABLE`.
 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: Record<string, unknown> | undefined
  readonly path: string
  readonly method: string

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.details = init.details
    this.path = init.path
    this.method = init.method
  }

  /** The denial reason inside `details`, present on quota and suppression errors. */
  get reason(): string | undefined {
    const value = this.details?.reason
    return typeof value === 'string' ? value : undefined
  }

  /** True when waiting or retrying could plausibly succeed. */
  get isTransient(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/** True when the error is one of the codes a page should explain rather than treat as a fault. */
export function isExpectedDenial(value: unknown): value is ApiError {
  return isApiError(value) && (value.code === 'QUOTA_DENIED' || value.code === 'SUPPRESSED')
}

// ---------------------------------------------------------------------------
// Session expiry broadcast
// ---------------------------------------------------------------------------

type UnauthorizedListener = () => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

/**
 * Called when the API says the session is gone on a request that was not itself
 * a sign-in attempt. `lib/session.ts` subscribes and clears the cached session
 * so the router lands on the sign-in screen.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/** Sign-in, sign-out and the refresh itself legitimately return 401, so they never trip the broadcast. */
const AUTH_PATHS = ['/auth/login', '/auth/totp', '/auth/logout', '/auth/totp/enroll', '/auth/refresh']

function isAuthPath(path: string): boolean {
  return AUTH_PATHS.some((p) => path.startsWith(p))
}

function announceUnauthorized(path: string): void {
  if (isAuthPath(path)) return
  for (const listener of unauthorizedListeners) listener()
}

// ---------------------------------------------------------------------------
// Staying signed in (0228)
// ---------------------------------------------------------------------------

/** The route that trades the refresh cookie for a new session. */
export const REFRESH_PATH = '/auth/refresh'

type RefreshedListener = (session: unknown) => void
const refreshedListeners = new Set<RefreshedListener>()

/** Told the new session body each time a refresh succeeds here, so the cached session is replaced. */
export function onSessionRefreshed(listener: RefreshedListener): () => void {
  refreshedListeners.add(listener)
  return () => refreshedListeners.delete(listener)
}

let refreshing: Promise<boolean> | null = null

/**
 * Get a new session from the refresh cookie, without asking for the password
 * (owner, 2026-11-03: "a refresh token mechanism so new login token can be issued
 * by the app itself on the frontend").
 *
 * One at a time: every request that comes back signed out while a refresh is on
 * its way waits for that one rather than starting its own, because a refresh
 * token is spent on first use. True when the browser now holds a working
 * session: renewed here, or by another tab a moment ago (the server's 409, after
 * which the browser already has the new cookies). False when there is nothing to
 * renew, and the person has to sign in.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshing !== null) return refreshing
  const attempt = (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (response.status === 409) return true
      if (!response.ok) return false
      const session = await readBody(response).catch(() => undefined)
      if (session !== undefined) for (const listener of refreshedListeners) listener(session)
      return true
    } catch {
      return false
    }
  })()
  refreshing = attempt
  void attempt.finally(() => {
    if (refreshing === attempt) refreshing = null
  })
  return attempt
}

// ---------------------------------------------------------------------------
// Request plumbing
// ---------------------------------------------------------------------------

export function buildQuery(params?: QueryParams): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item))
    } else {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function urlFor(path: string, params?: QueryParams): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${suffix}${buildQuery(params)}`
}

function envelopeFrom(payload: unknown): ErrorEnvelope['error'] | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined
  const wrapper = payload as { error?: unknown }
  if (typeof wrapper.error !== 'object' || wrapper.error === null) return undefined
  const error = wrapper.error as { code?: unknown; message?: unknown; details?: unknown }
  if (typeof error.code !== 'string') return undefined
  return {
    code: error.code as ErrorWireCode,
    message: typeof error.message === 'string' ? error.message : 'The request failed.',
    details:
      typeof error.details === 'object' && error.details !== null
        ? (error.details as Record<string, unknown>)
        : undefined,
  }
}

/**
 * What a 401 means when the server did not say.
 *
 * Exported because `errors.ts` has to tell this apart from a 401 that came with
 * a real sentence on it. A wrong password and an expired cookie are the same
 * status and want opposite words.
 */
export const SESSION_ENDED_MESSAGE = 'Your session has ended.'

const STATUS_FALLBACK: Record<number, { code: string; message: string }> = {
  400: { code: 'VALIDATION_FAILED', message: 'The request was rejected as invalid.' },
  401: { code: 'UNAUTHORIZED', message: SESSION_ENDED_MESSAGE },
  403: { code: 'FORBIDDEN', message: 'You do not have access to this.' },
  404: { code: 'NOT_FOUND', message: 'That is not here.' },
  409: { code: 'CONFLICT', message: 'That conflicts with the current state.' },
  413: { code: 'PAYLOAD_TOO_LARGE', message: 'That file is larger than the server accepts.' },
  422: { code: 'QUOTA_DENIED', message: 'The action was blocked by a limit.' },
  429: { code: 'RATE_LIMITED', message: 'Too many requests for now.' },
  500: { code: 'INTERNAL', message: 'The server hit an unexpected problem.' },
  502: { code: 'UPSTREAM_FAILED', message: 'A service the platform depends on did not answer.' },
  503: { code: 'UNAVAILABLE', message: 'The service is temporarily unavailable.' },
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined
  const type = response.headers.get('content-type') ?? ''
  const text = await response.text()
  if (text.length === 0) return undefined
  if (type.includes('json')) {
    try {
      return JSON.parse(text) as unknown
    } catch {
      return { __raw: text }
    }
  }
  return { __raw: text }
}

async function toApiError(response: Response, method: string, path: string): Promise<ApiError> {
  let payload: unknown
  try {
    payload = await readBody(response)
  } catch {
    payload = undefined
  }
  const envelope = envelopeFrom(payload)
  const fallback = STATUS_FALLBACK[response.status] ?? {
    code: 'INTERNAL',
    message: 'The request failed.',
  }
  return new ApiError({
    code: envelope?.code ?? fallback.code,
    message: envelope?.message ?? fallback.message,
    status: response.status,
    details: envelope?.details,
    method,
    path,
  })
}

export interface RequestOptions {
  params?: QueryParams
  signal?: AbortSignal
  /** Extra headers. `Idempotency-Key` is the usual reason to reach for this. */
  headers?: Record<string, string>
}

/**
 * The brand this page is working in when it is not the one the switcher is on.
 *
 * Read off `?brand=` in the address, so a campaign in another brand opens,
 * and the wizard makes one there, without moving the switcher. Every request
 * the page makes carries it, and the server only honours a brand the person
 * belongs to.
 */
export function pageBrand(search: string | null = typeof window === 'undefined' ? null : window.location.search): string | null {
  if (search === null) return null
  const value = new URLSearchParams(search).get('brand')
  return value !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? value : null
}

/** The header the server reads that brand from. */
export const PAGE_BRAND_HEADER = 'X-Brand-Id'

/**
 * Options that send one request into a named brand, for a row on a list that
 * spans several. Without it the request is read in the page's brand, where a
 * row from another brand does not exist. No brand, or an older server's row
 * without one, sends nothing extra, which is the page's brand as before.
 */
export function requestInBrand(brandId: string | null | undefined): RequestOptions {
  return brandId === null || brandId === undefined || brandId === '' ? {} : { headers: { [PAGE_BRAND_HEADER]: brandId } }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  /** `text` for the endpoints that answer markdown rather than JSON. */
  expect: 'json' | 'text' = 'json',
  /** Set on the one retry after a refresh, so a refused retry is final. */
  retried = false,
): Promise<T> {
  const url = urlFor(path, options.params)
  const brand = pageBrand()
  const headers: Record<string, string> = {
    Accept: expect === 'text' ? 'text/markdown, text/plain' : 'application/json',
    ...(brand === null ? {} : { [PAGE_BRAND_HEADER]: brand }),
    ...options.headers,
  }
  let payload: BodyInit | undefined

  if (body !== undefined) {
    if (body instanceof FormData) {
      // Let the browser set the multipart boundary.
      payload = body
    } else {
      headers['Content-Type'] = 'application/json'
      payload = JSON.stringify(body)
    }
  }

  let response: Response
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: payload,
      signal: options.signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError({
      code: 'NETWORK_UNREACHABLE',
      message: 'The app could not reach the server.',
      status: 0,
      method,
      path,
      cause,
    })
  }

  if (!response.ok) {
    const error = await toApiError(response, method, path)
    if (error.status === 401 && !isAuthPath(path)) {
      // Signed out on the server, perhaps only because the session ran out: renew
      // it from the refresh cookie and ask once more before giving up (0228).
      if (!retried && (await refreshSession())) return request<T>(method, path, body, options, expect, true)
      announceUnauthorized(path)
    }
    throw error
  }

  let parsed: unknown
  try {
    parsed = expect === 'text' ? await response.text() : await readBody(response)
  } catch (cause) {
    throw new ApiError({
      code: 'RESPONSE_UNREADABLE',
      message: 'The server answered with something this app could not read.',
      status: response.status,
      method,
      path,
      cause,
    })
  }
  return parsed as T
}

// ---------------------------------------------------------------------------
// Multipart upload with progress (CSV imports, data-export uploads)
// ---------------------------------------------------------------------------

export interface UploadOptions extends RequestOptions {
  /** Fraction from 0 to 1, or null while the total size is unknown. */
  onProgress?: (fraction: number | null) => void
}

/**
 * `fetch` cannot report upload progress, and a prospect import is a big enough
 * file that a silent wait feels broken, so uploads go through XHR.
 */
function upload<T>(path: string, form: FormData, options: UploadOptions = {}, retried = false): Promise<T> {
  const url = urlFor(path, options.params)
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.withCredentials = true
    xhr.responseType = 'text'
    xhr.setRequestHeader('Accept', 'application/json')
    for (const [key, value] of Object.entries(options.headers ?? {})) {
      xhr.setRequestHeader(key, value)
    }

    if (options.onProgress) {
      xhr.upload.onprogress = (event) => {
        options.onProgress?.(event.lengthComputable ? event.loaded / event.total : null)
      }
    }

    const fail = (code: string, message: string, status: number, details?: Record<string, unknown>) =>
      reject(new ApiError({ code, message, status, details, method: 'POST', path }))

    xhr.onerror = () => fail('NETWORK_UNREACHABLE', 'The upload could not reach the server.', 0)
    xhr.ontimeout = () => fail('NETWORK_UNREACHABLE', 'The upload timed out.', 0)
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))

    xhr.onload = () => {
      let parsed: unknown
      try {
        parsed = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : undefined
      } catch {
        parsed = undefined
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T)
        return
      }
      const envelope = envelopeFrom(parsed)
      const fallback = STATUS_FALLBACK[xhr.status] ?? { code: 'INTERNAL', message: 'The upload failed.' }
      if (xhr.status === 401 && !retried && !isAuthPath(path)) {
        // The same second chance a plain request gets, with the same file.
        void refreshSession().then((renewed) => {
          if (renewed) {
            upload<T>(path, form, options, true).then(resolve, reject)
            return
          }
          announceUnauthorized(path)
          fail(envelope?.code ?? fallback.code, envelope?.message ?? fallback.message, xhr.status, envelope?.details)
        })
        return
      }
      if (xhr.status === 401) announceUnauthorized(path)
      fail(
        envelope?.code ?? fallback.code,
        envelope?.message ?? fallback.message,
        xhr.status,
        envelope?.details,
      )
    }

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort()
      } else {
        options.signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }
    }

    xhr.send(form)
  })
}

/**
 * The typed client. Every page goes through this and nothing writes raw `fetch`.
 *
 * ```ts
 * const page = await api.get<Paginated<ProspectSummary>>('/prospects', { params: { q } })
 * await api.post('/campaigns/abc/launch')
 * ```
 */
export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('GET', path, undefined, options),
  /**
   * For the endpoints that answer markdown. Same failures, same `ApiError`,
   * same session-expiry broadcast, so a text endpoint cannot become the one
   * screen that renders a raw browser message or fails to sign somebody out.
   */
  text: (path: string, options?: RequestOptions): Promise<string> =>
    request<string>('GET', path, undefined, options, 'text'),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, body, options),
  del: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, undefined, options),
  upload: <T>(path: string, form: FormData, options?: UploadOptions): Promise<T> => upload<T>(path, form, options),
} as const
